import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { clearResponseAttemptDiagnostics } from '../utils/responseAttemptDiagnostics';
import { clearAllDrafts } from '../utils/draftStorage';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [assistantSettings, setAssistantSettings] = useState({ allowed_roles: [] });

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      await fetchAssistantSettings();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAssistantSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/assistant`);
      setAssistantSettings(response.data || { allowed_roles: [] });
    } catch (error) {
      console.error('Failed to fetch assistant settings:', error);
      setAssistantSettings({ allowed_roles: [] });
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;

    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    await fetchAssistantSettings();

    return userData;
  };

  const canAccessAssistant = () => {
    if (!user) return false;
    if (user.role_level === 1) return true;
    const allowedRoles = assistantSettings.allowed_roles || [];
    if (allowedRoles.length === 0) return false;
    const userRoleIds = user.role_ids?.length ? user.role_ids : [user.role_id].filter(Boolean);
    return userRoleIds.some(roleId => allowedRoles.includes(roleId));
  };

  const logout = () => {
    localStorage.removeItem('token');
    clearResponseAttemptDiagnostics();
    void clearAllDrafts();
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setAssistantSettings({ allowed_roles: [] });
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      login,
      logout,
      isLoading,
      assistantSettings,
      itinerariesEnabled: !!user?.itineraries_enabled,
      canViewItineraryDashboard: !!user?.can_view_itinerary_dashboard,
      canViewRecurringAgenda: !!user?.can_view_recurring_agenda,
      canAccessTrackingDashboard: !!user?.can_access_tracking_dashboard,
      canAccessAssistant,
      refreshAssistantSettings: fetchAssistantSettings
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
